import {css, StyleSheet} from 'aphrodite';
import React, {useContext, useRef} from 'react';

import {deleteAnalysisSteps, updateAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {TRANSFORMATION_STEP_TYPES} from 'analysis-tool/AnalysisToolConstants';
import {PILL_DISABLED_DUE_TO_DOWNSTREAM_FLAG_ROWS} from 'analysis-tool/AnalysisToolMessages';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import AnalysisClientStateIdContext from 'analysis-tool/ui/AnalysisClientStateIdContext';
import {recordAnalysisAction} from 'analysis-tool/ui/debugAnalysisActionHistory';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import TransformPillBase from 'data-frames/filters/components/TransformPillBase';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {formulaTextToHTML} from 'pipelines/formula/formulaUtil';
import InputAwareStepConfigEditorModal from 'pipelines/generalized-step-config-editor/InputAwareStepConfigEditorModal';
import {stepTypeIsConfigEditorSupported} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import hasDownstreamFlagRowsStep from 'pipelines/hasDownstreamFlagRowsStep';
import {DeletePipelineStepsBehavior} from 'pipelines/network/PipelineDAO';
import {DraftStepInputParents} from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL, PipelineStepConfig} from 'pipelines/PipelineTypes';
import {getChildSteps} from 'pipelines/StepLineageUtils';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useOnMount from 'ui-library/hooks/useOnMount';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as modals from 'util/Modals';
import {withLoadingScreen} from 'util/Promises';

function Formula({formulaHTML}: {formulaHTML: string}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useOnMount(() => {
    if (ref.current) {
      ref.current.innerHTML = formulaHTML;
    }
  });

  return <div className={css(styles.formula)} ref={ref} />;
}

interface TransformPillProps {
  analysis: PipelineGraphQL;
  step: GetAnalysisPipelineStep;
}

function getTooltipContentAndText(
  step: GetAnalysisPipelineStep,
  // inputDataFrameSpec may be null if the step or its ancestor(s) have not been configured and run yet. It's
  // only used if the step is SUCCEEDED
  inputDataFrameSpec: DataFrameSpec | null,
  computedColumnConfig: PipelineStepConfig<PipelineStepType.COMPUTED_COLUMN, 0> | null,
  childStepIsNotFinished: boolean
): {text: JSX.Element | string | null} {
  if (childStepIsNotFinished) {
    return {text: 'Disabled until the following step finishes running.'};
  } else if (computedColumnConfig != null) {
    if (step.statusCode === PipelineStepStatusCode.SUCCEEDED) {
      assert(inputDataFrameSpec != null, 'Succeeded step unexpectedly has a null inputDataFrameSpec', {
        stepId: step.id,
      });
      return {
        text: (
          <Formula
            formulaHTML={formulaTextToHTML(computedColumnConfig.formula.formulaText, inputDataFrameSpec)}
          />
        ),
      };
    } else if (step.statusCode === PipelineStepStatusCode.FAILED) {
      return {text: 'Invalid formula'};
    }
  } else if (step.statusCode !== PipelineStepStatusCode.SUCCEEDED) {
    return {text: step.statusMessage ?? 'Transform failed'};
  }
  return {text: null};
}

export function openTransformConfigModal(analysis: PipelineGraphQL, step: GetAnalysisPipelineStep): void {
  const {stepType, stepVersion} = step.definition;
  assert(stepTypeIsConfigEditorSupported(stepType, stepVersion), `Unsupported transform type ${stepType}`);

  const inputDataFrameSpec = {
    ...step.inputStepDatas[0].dataFrames[0],
    columns: step.inputStepDatas[0].dataFrames[0].allColumnsJson,
  };

  legacyRunModal(InputAwareStepConfigEditorModal, {
    stepType,
    stepVersion,
    inputDataFrameSpec,
    clientStateId: step.id,
    stepIsFailed: step.statusCode === PipelineStepStatusCode.FAILED,
    initialConfig:
      step.config == null
        ? undefined
        : step.statusCode === PipelineStepStatusCode.SUCCEEDED ||
            step.statusCode === PipelineStepStatusCode.FAILED
          ? getPipelineStepConfig(step, step.definition.stepType, step.definition.stepVersion)
          : undefined,
    analysisId: analysis.id,
    precedingStep: null,
    step,
    handleRequestSubmit: (
      newStepConfig: PipelineStepConfig<PipelineStepType, number>,
      stepInputParents: DraftStepInputParents | null
    ) =>
      withLoadingScreen(
        updateAndRunAnalysisStep(analysis.id, {
          stepId: step.id,
          stepConfig: newStepConfig,
          ...(stepInputParents == null ? {} : {stepInputParents}),
        }),
        'Updating transformations...'
      ),
  });
}

export default function TransformPill({analysis, step}: TransformPillProps): JSX.Element {
  const clientStateId = useContext(AnalysisClientStateIdContext);
  const {stepType, stepVersion} = step.definition;

  // Step may lack input data if it or its ancestor(s) haven't been configured/run yet
  const stepHasInputData = step.inputStepDatas[0].dataFrames[0] != null;
  const inputDataFrameSpec = stepHasInputData
    ? {
        ...step.inputStepDatas[0].dataFrames[0],
        columns: step.inputStepDatas[0].dataFrames[0].allColumnsJson,
      }
    : null;

  const handleClick =
    stepTypeIsConfigEditorSupported(stepType, stepVersion) && stepHasInputData
      ? () => {
          openTransformConfigModal(analysis, step);
        }
      : undefined;

  async function handleDelete(): Promise<void> {
    const stepIsFlagRows = step.definition.stepType === PipelineStepType.FLAG_ROWS;
    const stepHasSubsequentTransforms = getChildSteps(analysis, step).some((childStep) =>
      TRANSFORMATION_STEP_TYPES.includes(childStep.definition.stepType)
    );
    if (stepIsFlagRows) {
      await modals.confirm(
        'Delete flags?',
        <DeleteFlagRowsModalContent step={step} />,
        ButtonStyle.NEGATIVE,
        {
          acceptText: 'Delete flags',
          enableRedesign: true,
        }
      );
    } else if (stepHasSubsequentTransforms) {
      await modals.confirm(
        'Delete transform?',
        'All following transformations and filters will also be removed.',
        ButtonStyle.NEGATIVE,
        {enableRedesign: true}
      );
    }
    recordAnalysisAction('DELETE_TRANSFORM', {
      analysisId: analysis.id,
      stepId: step.id,
      stepType: step.definition.stepType,
    });
    await withLoadingScreen(
      (async () => {
        await deleteAnalysisSteps(
          analysis,
          {
            stepIds: [step.id],
            behavior: DeletePipelineStepsBehavior.DELETE_ALL_CHILD_TRANSFORMATION_STEPS,
          },
          clientStateId
        );
      })(),
      'Updating transformations...'
    );
  }

  const computedColumnConfig =
    stepType === PipelineStepType.COMPUTED_COLUMN && step.config != null
      ? getPipelineStepConfig(step, stepType, stepVersion)
      : null;

  const childStepIsNotFinished = getChildSteps(analysis, step).some(
    (childStep) =>
      childStep.statusCode !== PipelineStepStatusCode.SUCCEEDED &&
      childStep.statusCode !== PipelineStepStatusCode.FAILED
  );

  const contentAndText = getTooltipContentAndText(
    step,
    inputDataFrameSpec,
    computedColumnConfig,
    childStepIsNotFinished
  );

  const disabledDueToDownstreamFlagRowsStep = hasDownstreamFlagRowsStep(analysis, step);
  if (disabledDueToDownstreamFlagRowsStep) {
    contentAndText.text = (
      <>
        <div>{contentAndText.text}</div>
        <small>{PILL_DISABLED_DUE_TO_DOWNSTREAM_FLAG_ROWS}</small>
      </>
    );
  }

  return (
    <Tooltip
      toggleDisplayMode={ToggleDisplayMode.BLOCK_FIT_CONTENT}
      isDisabled={contentAndText.text == null}
      {...contentAndText}
    >
      <TransformPillBase
        key={step.id}
        text={
          computedColumnConfig == null
            ? STEP_TYPE_TO_USER_FACING_NAME[stepType]
            : `Add column: ${computedColumnConfig.newColumnName}`
        }
        onClick={handleClick}
        onDelete={handleDelete}
        isRunning={step.statusCode === PipelineStepStatusCode.RUNNING}
        disabled={
          isArchived(analysis) ||
          hasDownstreamFlagRowsStep(analysis, step) ||
          childStepIsNotFinished ||
          ![PipelineStepStatusCode.SUCCEEDED, PipelineStepStatusCode.FAILED].includes(step.statusCode)
        }
        hasError={step.statusCode === PipelineStepStatusCode.FAILED}
      />
    </Tooltip>
  );
}

/** @package */
export function getDeleteFlagRowsModalDisplayData(
  flaggingInfoList: Array<{
    isFlagged: boolean;
    comment: string;
    rowIndices: Array<number>;
  }>
): Array<{comment: string; rowIndices: Array<number>; isFlagged: boolean}> {
  const seenIndices = new Set<number>();
  const reversedIndexSets = flaggingInfoList.map(({rowIndices}) => new Set(rowIndices)).reverse();
  const reversedRowIndicesWithoutPreviouslySeen: Array<Array<number>> = [];
  // If I flag a row, unflag it, reflag it we don't want to count it multiple times
  reversedIndexSets.forEach((rowIndices) => {
    const rowIndicesWithoutPreviouslySeen = Array.from(rowIndices).filter((index) => !seenIndices.has(index));
    rowIndices.forEach((index) => seenIndices.add(index));
    reversedRowIndicesWithoutPreviouslySeen.push(rowIndicesWithoutPreviouslySeen);
  });
  const rowIndicesWithoutPreviouslySeen = reversedRowIndicesWithoutPreviouslySeen.reverse();
  return rowIndicesWithoutPreviouslySeen
    .map((rowIndices, i) => ({
      comment: flaggingInfoList[i].comment,
      rowIndices,
      isFlagged: flaggingInfoList[i].isFlagged,
    }))
    .filter(({rowIndices}) => rowIndices.length !== 0);
}

function DeleteFlagRowsModalContent({step}: {step: GetAnalysisPipelineStep}): JSX.Element {
  const flaggingInfoList = getPipelineStepConfig(step, PipelineStepType.FLAG_ROWS, 0).flaggingInfoList;
  const displayData = getDeleteFlagRowsModalDisplayData(flaggingInfoList);
  return (
    <>
      <p>
        Changes to this table will clear the following flags from downstream tables that depend on this data.
      </p>
      Comments to delete:
      <ul>
        {displayData.map(({comment, rowIndices, isFlagged}, i) => (
          <li key={i}>
            "{comment}" ({rowIndices.length} {isFlagged ? 'flagged' : 'commented'}{' '}
            {pluralize(rowIndices.length, 'row', 'rows')})
          </li>
        ))}
      </ul>
      <p>
        <BenchlingIcon icon="exclamation-triangle" className={css(styles.exclamationIcon)} /> These flags will
        be lost, and will only be viewable in audit logs
      </p>
    </>
  );
}

const styles = StyleSheet.create({
  formula: {
    fontFamily: FONT_FAMILY_MONOSPACED,
    fontSize: 16,
  },
  exclamationIcon: {
    color: Colors.ORANGE_LIGHT,
  },
});
